// Where a reference image's bytes come from (2B). The admin pages read them
// through the admin route; the client page through its slug, which the worker
// gates on what that client may see. Node views, the diff and the PDF all ask
// here, so a page sets its source once, before its editor renders.

import { useEffect } from 'react';

export type RefImageSource =
  | { kind: 'admin'; scriptId: string }
  | { kind: 'client'; slug: string };

let current: RefImageSource | null = null;

export function getRefImageSource(): RefImageSource | null {
  return current;
}

export function setRefImageSource(source: RefImageSource | null): void {
  current = source;
}

export function refImageUrl(imageId: string, source: RefImageSource | null = current): string | null {
  if (!source) return null;
  const id = encodeURIComponent(imageId);
  return source.kind === 'admin'
    ? `/api/admin/scripts/${encodeURIComponent(source.scriptId)}/images/${id}`
    : `/api/client/scripts/${encodeURIComponent(source.slug)}/images/${id}`;
}

/**
 * Set during render, not in an effect: the editor below builds its image
 * node views in the same commit, and child effects run before a parent's.
 * Setting a module value is idempotent, so a re-render is harmless.
 */
export function useRefImageSource(source: RefImageSource | null): void {
  setRefImageSource(source);
  const key = source ? `${source.kind}:${source.kind === 'admin' ? source.scriptId : source.slug}` : '';
  useEffect(() => {
    return () => {
      // Only clear what this page set; the next page may already own it.
      const now = current;
      const nowKey = now ? `${now.kind}:${now.kind === 'admin' ? now.scriptId : now.slug}` : '';
      if (nowKey === key) current = null;
    };
  }, [key]);
}

export interface UploadedImage {
  id: string;
  width: number;
  height: number;
}

export class ImageUploadError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

/** XHR rather than fetch, for upload progress. Admin only. */
export function uploadRefImage(
  scriptId: string,
  image: { blob: Blob; width: number; height: number },
  onProgress: (fraction: number) => void,
  replacesImageId?: string,
): Promise<UploadedImage> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.set('image', image.blob, 'image.jpg');
    form.set('width', String(image.width));
    form.set('height', String(image.height));
    if (replacesImageId) form.set('replacesImageId', replacesImageId);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/admin/scripts/${encodeURIComponent(scriptId)}/images`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: { id?: string; width?: number; height?: number; error?: string; code?: string } = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // Fall through to the generic failure below.
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.id) {
        resolve({ id: body.id, width: body.width ?? image.width, height: body.height ?? image.height });
      } else {
        reject(new ImageUploadError(body.error ?? 'Upload failed', xhr.status, body.code));
      }
    };
    xhr.onerror = () => reject(new ImageUploadError('Network error', 0));
    xhr.send(form);
  });
}
